import React from 'react';
import { ContentCard } from '../types';
import { useTop500PopularContent } from './usePopularContent';
import { useFeaturedContent, useMovies, useTVShows } from './useContentQueries';

interface UseHomepageContentResult {
  // Top 100 popular title IDs for filtering (backend limit)
  topPopularIds: Set<string>;
  
  // Filtered content sections
  filteredFeaturedContent: ContentCard[];
  filteredMovies: ContentCard[];
  filteredTVShows: ContentCard[];
  
  // Combined loading and error states
  isLoading: boolean;
  error: Error | null;
  
  // Individual section states for granular control
  sections: {
    popular: {
      isLoading: boolean;
      error: Error | null;
      refetch: () => void;
    };
    featured: {
      isLoading: boolean;
      error: Error | null;
      refetch: () => void;
    };
    movies: {
      isLoading: boolean;
      error: Error | null;
      refetch: () => void;
    };
    tvShows: {
      isLoading: boolean;
      error: Error | null;
      refetch: () => void;
    };
  };
  
  // Refetch all content
  refetchAll: () => void;
}

/**
 * Hook for managing homepage content filtering to top 100 popular titles
 * Integrates with existing content hooks and provides efficient filtering
 * Note: Limited to top 100 due to backend API constraints
 */
export const useHomepageContent = (): UseHomepageContentResult => {
  // Fetch top 100 popular titles for filtering
  const {
    popularTitleIds,
    isLoading: isPopularLoading,
    error: popularError,
    refetch: refetchPopular,
  } = useTop500PopularContent();

  // Fetch featured content
  const {
    data: featuredContent = [],
    isLoading: isFeaturedLoading,
    error: featuredError,
    refetch: refetchFeatured,
  } = useFeaturedContent();

  // Fetch movies with popularity sorting and filtering
  const {
    data: moviesResponse,
    isLoading: isMoviesLoading,
    error: moviesError,
    refetch: refetchMovies,
  } = useMovies(1, 100, { 
    sortBy: 'popularity',
    popularTitleIds: popularTitleIds
  }); // Fetch more to ensure we have enough after filtering

  // Fetch TV shows with popularity sorting and filtering
  const {
    data: tvShowsResponse,
    isLoading: isTVShowsLoading,
    error: tvShowsError,
    refetch: refetchTVShows,
  } = useTVShows(1, 100, { 
    sortBy: 'popularity',
    popularTitleIds: popularTitleIds
  }); // Fetch more to ensure we have enough after filtering

  // Extract movies and TV shows data from API responses
  const movies = moviesResponse?.data || [];
  const tvShows = tvShowsResponse?.data || [];

  // Use featured content directly since backend already applies family-friendly filtering
  const filteredFeaturedContent = React.useMemo(() => {
    // Featured content is already filtered by the backend for family-friendly content
    // No need to apply additional popularity filtering here
    return featuredContent;
  }, [featuredContent]);

  // Movies and TV shows are already filtered at the API level, so we can use them directly
  const filteredMovies = React.useMemo(() => {
    return movies || [];
  }, [movies]);

  const filteredTVShows = React.useMemo(() => {
    return tvShows || [];
  }, [tvShows]);

  // Combined loading state - true if any section is loading
  const isLoading = isPopularLoading || isFeaturedLoading || isMoviesLoading || isTVShowsLoading;

  // Combined error state - first error encountered, prioritizing popular content errors
  const error = popularError || featuredError || moviesError || tvShowsError;

  // Refetch all content sections
  const refetchAll = React.useCallback(() => {
    refetchPopular();
    refetchFeatured();
    refetchMovies();
    refetchTVShows();
  }, [refetchPopular, refetchFeatured, refetchMovies, refetchTVShows]);

  return {
    topPopularIds: popularTitleIds,
    filteredFeaturedContent,
    filteredMovies,
    filteredTVShows,
    isLoading,
    error,
    sections: {
      popular: {
        isLoading: isPopularLoading,
        error: popularError,
        refetch: refetchPopular,
      },
      featured: {
        isLoading: isFeaturedLoading,
        error: featuredError,
        refetch: refetchFeatured,
      },
      movies: {
        isLoading: isMoviesLoading,
        error: moviesError,
        refetch: refetchMovies,
      },
      tvShows: {
        isLoading: isTVShowsLoading,
        error: tvShowsError,
        refetch: refetchTVShows,
      },
    },
    refetchAll,
  };
};

export default useHomepageContent;